import React, { useEffect, useState } from 'react';
import { loadTossPayments, ANONYMOUS } from '@tosspayments/tosspayments-sdk';
import './TossCheckout.scss';

interface TossCheckoutProps {
    clientKey: string;  // Client key from server
    orderid: string;
    orderName: string;
    amount: number;
    currency: string;
    customerName: string;
    customerEmail: string;
    customerPhone?: string;
    successUrl: string;
    failUrl: string;
    isTestMode?: boolean;
    onCancel: () => void;
}

const TossCheckout: React.FC<TossCheckoutProps> = ({
    clientKey,
    orderid,
    orderName,
    amount,
    currency,
    customerName,
    customerEmail,
    customerPhone,
    successUrl,
    failUrl,
    isTestMode = false,
    onCancel
}) => {
    const [ready, setReady] = useState(false);
    const [widgets, setWidgets] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        async function initializePaymentWidgets() {
            try {
                // Initialize Toss Payments with server-provided client key
                const tossPayments = await loadTossPayments(clientKey);
                
                // Create widgets for anonymous (non-member) checkout
                const paymentWidgets = tossPayments.widgets({ 
                    customerKey: ANONYMOUS 
                });
                
                setWidgets(paymentWidgets);
            } catch (err: any) {
                console.error('Failed to initialize Toss Payments:', err);
                setError(err.message || 'Failed to initialize payment');
            }
        }

        initializePaymentWidgets();
    }, [clientKey]);

    useEffect(() => {
        async function renderPaymentWidgets() {
            if (!widgets) return;

            try {
                // Set payment amount
                await widgets.setAmount({
                    currency: currency || 'KRW',
                    value: amount
                });

                // Render payment methods UI
                await widgets.renderPaymentMethods({
                    selector: "#toss-payment-method",
                    variantKey: "DEFAULT",
                });

                // Render agreement UI
                await widgets.renderAgreement({
                    selector: "#toss-agreement",
                    variantKey: "AGREEMENT",
                });

                setReady(true);
            } catch (err: any) {
                console.error('Failed to render payment widgets:', err);
                setError(err.message || 'Failed to render payment UI');
            }
        }

        renderPaymentWidgets();
    }, [widgets, amount, currency]);

    const handlePayment = async () => {
        if (!widgets || !ready) return;

        try {
            // Request payment
            await widgets.requestPayment({
                orderId: orderid,
                orderName: orderName,
                successUrl: successUrl,
                failUrl: failUrl,
                customerEmail: customerEmail,
                customerName: customerName,
                customerMobilePhone: customerPhone,
            });
        } catch (err: any) {
            console.error('Payment request failed:', err);
            setError(err.message || 'Payment request failed');
        }
    };

    const formatAmount = () => {
        const formatter = new Intl.NumberFormat('ko-KR', {
            style: 'currency',
            currency: currency || 'KRW',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
        return formatter.format(amount);
    };

    if (error) {
        return (
            <div className="toss-checkout-container">
                <div className="toss-error">
                    <h3>{"Payment Error"}</h3>
                    <p>{error}</p>
                    <button onClick={onCancel} className="cancel-button">
                        {"Go Back"}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="toss-checkout-container">
            <div className="toss-payment-info">
                <h3>{"Payment Details"}</h3>
                <div className="payment-summary">
                    <div className="summary-row">
                        <span className="label">{"Product:"}</span>
                        <span className="value">{orderName}</span>
                    </div>
                    <div className="summary-row">
                        <span className="label">{"Amount:"}</span>
                        <span className="value amount">{formatAmount()}</span>
                    </div>
                    {isTestMode && (
                        <p className="test-mode-notice">
                            {"(Test Mode - No actual payment will be processed)"}
                        </p>
                    )}
                </div>
            </div>

            {/* Toss Payment Widgets */}
            <div className="toss-widgets">
                <div id="toss-payment-method" className="payment-method-widget" />
                <div id="toss-agreement" className="agreement-widget" />
            </div>

            {/* Action Buttons */}
            <div className="toss-actions">
                <button 
                    className="payment-button"
                    onClick={handlePayment}
                    disabled={!ready}
                >
                    {ready ? "Complete Payment" : "Loading..."}
                </button>
                <button 
                    className="cancel-button"
                    onClick={onCancel}
                >
                    {"Cancel"}
                </button>
            </div>
        </div>
    );
};

export default TossCheckout;